import { useCallback, useEffect, useMemo, useState } from "react";

import { ListeningHistoryItem, HistoryItemType } from "../lib/history/types";

const STORAGE_KEY = "streameater.history.v1";
const MAX_ITEMS = 100;

const isBrowser = typeof window !== "undefined";

const parseHistory = (raw: string | null): ListeningHistoryItem[] => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((item) => item && item.id && item.type && item.title)
      .map((item) => ({
        ...item,
        firstPlayedAt:
          item.firstPlayedAt || item.lastPlayedAt || new Date().toISOString(),
        lastPlayedAt:
          item.lastPlayedAt || item.firstPlayedAt || new Date().toISOString(),
      })) as ListeningHistoryItem[];
  } catch (err) {
    console.warn(
      "[useListeningHistory] Failed to parse history, resetting.",
      err,
    );
    return [];
  }
};

export function useListeningHistory() {
  const [history, setHistory] = useState<ListeningHistoryItem[]>(() => {
    if (!isBrowser) {
      return [];
    }
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      const parsed = parseHistory(stored);
      const sorted = parsed.sort(
        (a, b) =>
          new Date(b.lastPlayedAt).getTime() -
          new Date(a.lastPlayedAt).getTime(),
      );
      return sorted;
    } catch (e) {
      console.error(
        "[useListeningHistory] Failed to load history from localStorage:",
        e,
      );
      return [];
    }
  });

  // Sync across tabs
  useEffect(() => {
    if (!isBrowser) return;
    const handleStorage = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY) {
        const parsed = parseHistory(event.newValue);
        setHistory(
          parsed.sort(
            (a, b) =>
              new Date(b.lastPlayedAt).getTime() -
              new Date(a.lastPlayedAt).getTime(),
          ),
        );
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const persist = useCallback((items: ListeningHistoryItem[]) => {
    if (!isBrowser) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (err) {
      console.warn("[useListeningHistory] Failed to persist history", err);
    }
  }, []);

  const addOrUpdateItem = useCallback(
    (
      partialItem: Partial<ListeningHistoryItem> & {
        id: string;
        type: HistoryItemType;
        title: string;
      },
    ) => {
      const now = new Date().toISOString();
      setHistory((prev) => {
        const existingIdx = prev.findIndex(
          (item) =>
            item.id === partialItem.id && item.type === partialItem.type,
        );

        let next: ListeningHistoryItem[];
        if (existingIdx !== -1) {
          const existing = prev[existingIdx];
          const updated: ListeningHistoryItem = {
            ...existing,
            ...partialItem,
            lastPlayedAt: now,
          };
          next = [updated, ...prev.filter((_, idx) => idx !== existingIdx)];
        } else {
          const created: ListeningHistoryItem = {
            id: partialItem.id,
            type: partialItem.type,
            title: partialItem.title,
            subtitle: partialItem.subtitle,
            source: partialItem.source,
            imageUrl: partialItem.imageUrl,
            country: partialItem.country,
            region: partialItem.region,
            genres: partialItem.genres,
            firstPlayedAt: now,
            lastPlayedAt: now,
            lastPositionSeconds: partialItem.lastPositionSeconds,
          };
          next = [created, ...prev];
        }

        // Deduplicate beyond id+type guard and cap
        const capped = next.slice(0, MAX_ITEMS);
        persist(capped);
        return capped;
      });
    },
    [persist],
  );

  const clearHistory = useCallback(() => {
    setHistory([]);
    if (isBrowser) {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const getRecentItems = useCallback(
    (limit: number) => {
      return history.slice(0, limit);
    },
    [history],
  );

  const sortedHistory = useMemo(
    () =>
      [...history].sort(
        (a, b) =>
          new Date(b.lastPlayedAt).getTime() -
          new Date(a.lastPlayedAt).getTime(),
      ),
    [history],
  );

  return {
    history: sortedHistory,
    addOrUpdateItem,
    clearHistory,
    getRecentItems,
  };
}
